import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import { cn, getAvatarDecorationUrl, getAvatarUrl } from '~/util';
import { allBadgesMap } from '~/util/types/badges';
import { ProfileMerged } from '~/util/types/profiles';
import { BaseUser, ExtendedBaseUser, User } from '~/util/types/users';
import { BadgeIcon } from './BadgeIcon';
import { BadgeInfo } from './BadgeInfo';

export const UserProfilePicture = ({
    user,
    className,
    onBadgeClick,
    showBadgeIfNull = false,
    smallBadge = false,
}: {
    user: BaseUser | ExtendedBaseUser | User | ProfileMerged;
    className?: string;
    onBadgeClick?: () => void;
    showBadgeIfNull?: boolean;
    smallBadge?: boolean;
}) => {
    const avatarUrl = getAvatarUrl(user);
    const avatarDecorationUrl = getAvatarDecorationUrl(user);

    const featuredBadge =
        'featured_badge_code' in user && user.featured_badge_code
            ? user.featured_badge_code
            : null;

    const featuredBadgeData = featuredBadge
        ? allBadgesMap.get(featuredBadge)
        : null;

    const additionalBadgeData =
        'badges' in user
            ? user.badges?.find((badge) => badge.badge_code === featuredBadge)
            : undefined;

    const shouldRenderBadge =
        (!!featuredBadge && !!featuredBadgeData) || showBadgeIfNull;

    const badge =
        featuredBadge && featuredBadgeData
            ? {
                  badge_code: featuredBadge,
                  ...featuredBadgeData,
                  ...additionalBadgeData,
              }
            : undefined;

    return (
        <div
            className={cn(
                'relative inline-block shrink-0 overflow-visible',
                className,
            )}
        >
            <img
                src={avatarUrl}
                alt="Avatar"
                className={cn('size-9 rounded-full w-full h-full')}
                onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = '/assets/avatar_default.svg';
                }}
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
            />
            {avatarDecorationUrl && (
                <img
                    src={avatarDecorationUrl}
                    alt="Discord Avatar Decoration"
                    className="pointer-events-none absolute left-1/2 top-1/2 z-10 size-[120%] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
                />
            )}

            {shouldRenderBadge && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            aria-label={
                                featuredBadgeData?.title ?? 'No badge selected'
                            }
                            className={cn(
                                'absolute z-20 rounded-full object-cover p-0 border-0 bg-transparent',
                                smallBadge
                                    ? '-bottom-2/10 -right-2/10 size-3/5'
                                    : '-bottom-3/10 -right-3/10 size-4/5',
                                onBadgeClick &&
                                    'cursor-pointer hover:scale-110 transition-transform',
                            )}
                            onClick={onBadgeClick}
                        >
                            {featuredBadgeData ? (
                                <BadgeIcon
                                    badge={featuredBadgeData}
                                    className="h-full w-full"
                                />
                            ) : (
                                <img
                                    src="/assets/badge-icons/edit.webp"
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </button>
                    </TooltipTrigger>
                    {badge && (
                        <TooltipContent
                            side="right"
                            className="bg-background-light max-w-lg overflow-visible"
                        >
                            <BadgeInfo
                                badge={badge}
                                showCompletionStatus={false}
                            />
                        </TooltipContent>
                    )}
                </Tooltip>
            )}
        </div>
    );
};
